/**
 * 页面水印的纯计算逻辑（姓名截断 + SVG 平铺图生成）。
 *
 * 规则全部来自需求附件《页面水印 · 水印规范 v1.0》，改动前请先对规范。
 * 这里只做纯函数，不碰 DOM，便于单测覆盖。
 */

/** 水印信息，字段与后端 currentUserWatermark 一一对应 */
export interface WatermarkInfo {
    enabled: boolean;
    /** 人员ID，溯源唯一主键 */
    userId: string;
    /** 人员姓名，后端原样下发，截断在前端做 */
    userName: string;
    /** 账号末4位；账号无手机号时为空串 */
    accountTail: string;
    /** 服务端日期，YYYY-MM-DD */
    date: string;
}

/** 姓名显示宽度上限：英文/数字/符号计 1，汉字计 2 */
const NAME_WIDTH_LIMIT = 10;
/** 姓名汉字数量上限 */
const NAME_CJK_LIMIT = 4;
const ELLIPSIS = '...';

/** 汉字及日韩字符，与规范给出的判定一致 */
const CJK_PATTERN = /[一-龥぀-ヿ가-힯]/;

/** 平铺单元（标准档）。稀疏 400×240/15px、密集 220×130/12px 暂不开放配置 */
export const WATERMARK_TILE = { width: 300, height: 175, fontSize: 13 } as const;
/** 品牌深紫，深浅背景下均可辨识 */
export const WATERMARK_COLOR = '#3d2a63';
/** 默认不透明度，肉眼可辨但不干扰数据阅读 */
export const WATERMARK_OPACITY = 0.1;
/** 斜向排布角度，裁剪后仍可读 */
export const WATERMARK_ROTATE_DEG = -24;

/**
 * 按「显示宽度」截断姓名。姓名**不脱敏**，只限长。
 *
 * 宽度上限 10（汉字计 2、其余计 1），同时汉字数不超过 4。
 * 超限截断后以 `...` 结尾；截断处末尾若是空格先去掉再拼 `...`。
 *
 * 用 `for...of` 而非索引遍历：它按码点迭代，emoji 等代理对不会被劈成两半。
 */
export function maskName(raw: string | null | undefined): string {
    const name = String(raw ?? '').trim();
    if (!name) return '';

    let width = 0;
    let cjkCount = 0;
    let out = '';

    for (const ch of name) {
        const isCjk = CJK_PATTERN.test(ch);
        const charWidth = isCjk ? 2 : 1;
        const nextCjkCount = cjkCount + (isCjk ? 1 : 0);
        if (width + charWidth > NAME_WIDTH_LIMIT || nextCjkCount > NAME_CJK_LIMIT) {
            return out.replace(/\s+$/, '') + ELLIPSIS;
        }
        width += charWidth;
        cjkCount = nextCjkCount;
        out += ch;
    }
    return out;
}

/**
 * 第 1 行：`人员ID 姓名 账号后4位`，字段间单个空格。
 *
 * 用 filter 拼接而不是模板串：账号无手机号时 accountTail 是空串，
 * 直接拼会留一个尾随空格（截图上看是「1234 杨明 」，像少了点什么）。
 */
export function buildLine1(info: WatermarkInfo): string {
    return [info.userId, maskName(info.userName), info.accountTail]
        .filter((part) => part !== '')
        .join(' ');
}

/**
 * XML 文本转义。
 *
 * 规范给的参考实现没做这步，但姓名是用户可控内容：出现 `&` 或 `<`
 * 会让 SVG 变成非法 XML，浏览器直接不渲染背景图 —— 水印**静默消失**，
 * 而这恰恰发生在「名字里有特殊字符」的那个人身上，最难被发现。
 */
function escapeXml(text: string): string {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');
}

/**
 * 生成两行水印的 SVG 平铺图，返回可直接用于 CSS `background-image` 的值。
 *
 * 必须整体 `encodeURIComponent`，且**不能**写成 `data:image/svg+xml;utf8,`：
 * 分号会截断 CSS 属性值，水印同样是静默失效。单测里锁住了这两点。
 */
export function buildWatermarkBackgroundImage(
    info: WatermarkInfo,
    opacity: number = WATERMARK_OPACITY
): string {
    const { width, height, fontSize } = WATERMARK_TILE;
    const line1 = escapeXml(buildLine1(info));
    const line2 = escapeXml(info.date);
    const cx = width / 2;
    const cy = height / 2;

    const svg =
        `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">` +
        `<g transform="rotate(${WATERMARK_ROTATE_DEG} ${cx} ${cy})" fill="${WATERMARK_COLOR}" ` +
        `fill-opacity="${opacity}" ` +
        `font-family="Inter, PingFang SC, Microsoft YaHei, sans-serif" ` +
        `font-size="${fontSize}" font-weight="500" text-anchor="middle">` +
        `<text x="${cx}" y="${cy - 3}" letter-spacing="1.2">${line1}</text>` +
        `<text x="${cx}" y="${cy + fontSize + 3}" letter-spacing="1.2" font-weight="400">${line2}</text>` +
        `</g></svg>`;

    return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

/** 平铺尺寸，与 SVG 单元一致，供 CSS `background-size` 使用 */
export function buildWatermarkBackgroundSize(): string {
    return `${WATERMARK_TILE.width}px ${WATERMARK_TILE.height}px`;
}

/**
 * 水印是否应当渲染。
 *
 * 开关关闭、或身份字段全空（拿不到登录态）时都不渲染 ——
 * 只有日期没有身份信息的水印毫无溯源价值，反而干扰阅读。
 */
export function shouldRenderWatermark(info: WatermarkInfo | null): info is WatermarkInfo {
    if (info == null || !info.enabled) return false;
    return info.userId !== '' || info.userName !== '';
}

/**
 * 当前浏览器的 IANA 时区，用于让后端决定水印日期按哪个时区取。
 *
 * 后端拿到什么时区就按什么时区算当日日期，解析不了才回落美西，
 * 所以这里拿到什么都原样上送，不在前端做判断 —— 判断逻辑放一处就够了。
 *
 * 包 try/catch：Intl 在个别老环境下不可用，拿不到就上送 null，后端回落美西。
 */
export function getClientTimeZone(): string | null {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    } catch {
        return null;
    }
}
